import type { ComponentType } from 'react';
import Tooltip from './Tooltip';

export interface SegmentedOption<K extends string = string> {
  key: K;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  /** Tooltip / a11y text. Falls back to the label. */
  title?: string;
}

export interface SegmentedToggleProps<K extends string = string> {
  options: SegmentedOption<K>[];
  value: K;
  onChange: (key: K) => void;
  /** Greys the whole control out — e.g. the map board while pins are being edited. */
  disabled?: boolean;
  /** Reason shown on hover when disabled, so the control isn't dead for no visible reason. */
  disabledTitle?: string;
  className?: string;
  'aria-label'?: string;
}

/**
 * Compact segmented control for switching a page between two (or more) presentations of the
 * SAME data — as opposed to PageTabs, which switches between different surfaces.
 *
 * Sized to sit inside a page toolbar next to the title, so labels collapse to icons on narrow
 * screens rather than wrapping the toolbar onto a second line. Rendered as a radiogroup: it
 * chooses a view, it does not navigate, so tabs/links would both be the wrong semantics.
 */
export default function SegmentedToggle<K extends string = string>({
  options,
  value,
  onChange,
  disabled = false,
  disabledTitle,
  className = '',
  'aria-label': ariaLabel,
}: SegmentedToggleProps<K>) {
  if (options.length < 2) return null;
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex flex-none items-center gap-0.5 rounded-lg border border-border bg-muted/50 p-0.5 ${
        disabled ? 'opacity-50' : ''
      } ${className}`}
    >
      {options.map((o) => {
        const active = o.key === value;
        const Icon = o.icon;
        // The tooltip lives on Tooltip's wrapper, not the button: a disabled button emits no
        // mouse events at all, so `pointer-events-none` hands the hover to the wrapper and
        // the "why is this dead" reason still shows.
        return (
          <Tooltip key={o.key} label={disabled ? disabledTitle : o.title || o.label} side="bottom">
            <button
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={o.label}
              disabled={disabled}
              onClick={() => !active && onChange(o.key)}
              // The selected segment is BRAND-filled, not just a lighter card: this control now
              // sits in the middle of the toolbar with no title beside it, so "which view am I
              // on" is the one question it has to answer from across a room. A subtle raised
              // panel wasn't reading as selected at all against the bar's own card surface.
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors disabled:pointer-events-none sm:text-[13px] ${
                active
                  ? 'bg-brand text-white shadow-sm ring-1 ring-inset ring-white/10'
                  : 'text-muted-foreground hover:bg-card hover:text-foreground'
              }`}
            >
              {Icon && <Icon className="h-4 w-4 flex-none" />}
              {/* Dropped on the narrowest screens, where the symbol carries the meaning on
                  its own and the tooltip has the rest. */}
              <span className="hidden whitespace-nowrap sm:inline">{o.label}</span>
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
